'use client'

import { LinePlayCircleVideo } from '@qr-front/icons/line-play-circle-video'
import { LinePlayCircleVideoSmall } from '@qr-front/icons/line-play-circle-video-small'
import { FileProgress } from '@qr-front/ui/components/FileUpload'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn } from '@qr-front/ui/utils'
import { isFileUploadingAtom } from 'components/FileUploadToMediaStorage'
import { useAtomValue } from 'jotai'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { buildResourceUrl, TUploadedMedia } from 'services/mediaStorage'
import { sanitizeVideoUrl } from 'utils/sanitizeVideoUrl'

import { progressAtom } from '../useVideo'
import { ACCEPTED_MIME_TYPES } from '../VideoForm'
import { TVideo } from '../VideoFormModel'

const ReactPlayer = dynamic(
	() => import('react-player').then((mod) => mod.default),
	{
		ssr: false,
	},
)

export type VideoPlayerSize = 'normal' | 'small'

export const VideoPlayerSizes = {
	normal: {
		width: 270,
		height: 162,
	},
	small: {
		width: 120,
		height: 80,
	},
	preview: {
		width: 220,
		height: 130,
	},
}

const CustomPlayButton = ({
	size,
	onClick,
}: {
	size: VideoPlayerSize
	onClick: () => void
}) => {
	return (
		<div
			style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
			className={`absolute ${
				size === 'small' ? 'bottom-2.5 left-2.5 rounded-4 p-0.5' : 'inset-0'
			} z-[1] flex cursor-pointer items-center justify-center`}
			onClick={onClick}
		>
			{size === 'small' ? (
				<LinePlayCircleVideoSmall
					className="text-white"
					color="white"
					fill="black"
				/>
			) : (
				<LinePlayCircleVideo
					className="text-white"
					color="white"
					fill="black"
				/>
			)}
		</div>
	)
}

const getVideoLink = (media: TVideo): string => {
	if (media?.video?.source?.uploaded?.publicId) {
		return buildResourceUrl(media.video.source.uploaded as TUploadedMedia)
	}
	const url = media?.video?.source?.url || ''
	return sanitizeVideoUrl(url)
}

export const VideoPlayer = ({
	item,
	size = 'normal',
	previewMode = false,
}: {
	item: TVideo
	size?: VideoPlayerSize
	previewMode?: boolean
}) => {
	const isAndroid =
		typeof window !== 'undefined' && /android/i.test(navigator?.userAgent)
	const [videoReady, setVideoReady] = useState(false)
	const [canPlayFn, setCanPlayFn] = useState<((url: string) => boolean) | null>(
		null,
	)
	const [canPlay, setCanPlay] = useState(false)
	const [isPlaying, setIsPlaying] = useState(false)
	const [showControls, setShowControls] = useState(false)
	const progress = useAtomValue(progressAtom)
	const isFileUploading = useAtomValue(isFileUploadingAtom)

	const progressValue =
		progress && (progress as Record<string, number>)[item.video.name]

	const isUploading =
		(typeof progressValue === 'number' &&
			progressValue !== 100 &&
			isFileUploading) ||
		!item.video.source.uploaded

	const videoUrl = getVideoLink(item)
	const isCustomSize = isAndroid && videoUrl && size === 'normal'

	const isCurrentVideoUploading =
		isUploading && !videoUrl && progressValue !== 100

	const width = isCustomSize ? 216 : VideoPlayerSizes[size].width
	const height = isCustomSize ? 130 : VideoPlayerSizes[size].height

	const previewWidth = VideoPlayerSizes.preview.width
	const previewHeight = VideoPlayerSizes.preview.height

	const hasNameOrDescription = item.video.name || item.video.description

	const onVideoReady = () => {
		setVideoReady(true)
		// Show controls immediately when video is ready for Vimeo URLs
		if (videoUrl.includes('vimeo.com')) {
			setShowControls(true)
		}
	}

	useEffect(() => {
		Promise.all([
			import('react-player/youtube'),
			import('react-player/vimeo'),
			import('react-player/file'),
		]).then(([YouTube, Vimeo, FilePlayer]) => {
			setCanPlayFn(() => (url: string) => {
				return (
					YouTube.default.canPlay(url) ||
					Vimeo.default.canPlay(url) ||
					FilePlayer.default.canPlay(url)
				)
			})
		})
	}, [])

	useEffect(() => {
		setCanPlay(
			!!(
				!isCurrentVideoUploading &&
				((canPlayFn && canPlayFn?.(videoUrl)) ||
					(item.video.source.uploaded?.resourceType &&
						ACCEPTED_MIME_TYPES.includes(
							item.video.source.uploaded?.resourceType,
						)))
			),
		)
		console.log('[video player]canPlay', { canPlay, video: item.video })
	}, [
		canPlayFn,
		videoUrl,
		isCurrentVideoUploading,
		item.video.source.uploaded?.resourceType,
	])

	return (
		<div
			className={cn(
				'relative h-full min-h-20 w-full min-w-30 overflow-hidden rounded-4',
				hasNameOrDescription && size === 'normal' && 'rounded-b-none',
			)}
		>
			{typeof window !== 'undefined' && canPlay && (
				<ReactPlayer
					url={videoUrl}
					controls={showControls || isPlaying}
					width={previewMode ? previewWidth : width}
					height={previewMode ? previewHeight : height}
					onReady={onVideoReady}
					playing={isPlaying}
					config={
						videoUrl.includes('vimeo.com')
							? {
									vimeo: {
										playerOptions: {
											controls: true,
											autoplay: false,
											responsive: true,
										},
									},
								}
							: undefined
					}
				/>
			)}
			{isCurrentVideoUploading && !videoUrl && progressValue !== 100 && (
				<FileProgress progress={progressValue || 0} small />
			)}

			{!isPlaying && !isCurrentVideoUploading && !showControls && (
				<CustomPlayButton
					size={size}
					onClick={() => {
						setIsPlaying(true)
						// For non-Vimeo videos, show controls when playing starts
						if (!videoUrl.includes('vimeo.com')) {
							setShowControls(true)
						}
					}}
				/>
			)}

			{!videoReady && !isCurrentVideoUploading && (
				<Spinner className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
			)}
		</div>
	)
}
